import { isEqual } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useEffect, useState } from 'react';

import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';
import { RouterLink } from 'src/routes/components';

import { hasPermission } from 'src/utils/has-permissions';

import { useAuthContext } from 'src/auth/hooks';
import { AuthPermissions } from 'src/auth/permissions';
import { useAppDispatcher, useAppSelector } from 'src/redux/store';
import {
  deleteStationeryPurchaseOrder,
  getStationeryPurchaseOrders,
  removeStationeryPurchaseOrder,
} from 'src/redux/slices/stationery-purchase-order';

import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { useSettingsContext } from 'src/components/settings';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
import TableErrorData from 'src/components/table/table-error-data';
import {
  TableHeadCustom,
  TableNoData,
  TablePaginationCustom,
  TableSkeleton,
  useTable,
} from 'src/components/table';

import {
  IStationeryPurchaseOrderListItem,
  IStationeryPurchaseOrderTableFilters,
  IStationeryPurchaseOrderTableFilterValue,
  IStationeryPurchaseOrderTableSort,
} from 'src/types/stationery-purchase-order';

import StationeryPurchaseOrderTableRow from '../stationery-purchase-order-table-row';
import StationeryPurchaseOrderTableToolbar from '../stationery-purchase-order-table-toolbar';

const TABLE_HEAD = [
  { id: 'orderCode', label: 'Order #', align: 'left' },
  { id: 'purchaseDate', label: 'Purchase Date', align: 'left' },
  { id: 'supplier', label: 'Supplier', align: 'left' },
  { id: 'totalAfterDiscount', label: 'Total', align: 'right' },
  { id: 'updatedAt', label: 'Updated', align: 'left' },
  { id: '' },
];

const defaultFilters: IStationeryPurchaseOrderTableFilters = {
  generalSearch: '',
  startDate: null,
  endDate: null,
};

// Sorted by createdAt (not updatedAt) so the top row on an unfiltered first
// page always matches the backend's "most recently created" rule — that's
// the only row edit/delete is enabled for.
const defaultSortBy: IStationeryPurchaseOrderTableSort = {
  sortBy: 'createdAt',
  order: 'desc',
};

export default function StationeryPurchaseOrderListView() {
  const { status, orders, error } = useAppSelector((state) => state.stationeryPurchaseOrder);

  const { user: authUser } = useAuthContext();
  const table = useTable({
    defaultRowsPerPage: 25,
  });

  const dispatch = useAppDispatcher();

  const { page, rowsPerPage } = table;

  const settings = useSettingsContext();

  const denseHeight = table.dense ? 60 : 80;

  const router = useRouter();

  const [filters, setFilters] = useState(defaultFilters);

  const [sort, setSort] = useState(defaultSortBy);

  const canReset = !isEqual(defaultFilters, filters);

  // Edit/delete is only meaningful when viewing the true, unfiltered,
  // createdAt-desc-sorted first page — otherwise which row is "the last
  // order" can't be determined client-side.
  const canDetermineLastOrder =
    page === 0 &&
    sort.sortBy === 'createdAt' &&
    sort.order === 'desc' &&
    !filters.generalSearch &&
    !filters.startDate &&
    !filters.endDate;

  const handleFilters = useCallback(
    (name: string, value: IStationeryPurchaseOrderTableFilterValue) => {
      table.onResetPage();

      setFilters((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    },
    [table]
  );

  const handleEditRow = useCallback(
    (id: string) => {
      router.push(paths.dashboard.stationeryPurchaseOrder.edit(id));
    },
    [router]
  );

  const handleResetFilters = useCallback(() => {
    setFilters(defaultFilters);
  }, []);

  const fetchPaginatedOrders = useCallback(() => {
    dispatch(
      getStationeryPurchaseOrders({
        page,
        limit: rowsPerPage,
        generalSearch: filters.generalSearch,
        startDate: filters.startDate,
        endDate: filters.endDate,
        sort: sort.order,
        sortBy: sort.sortBy,
      })
    );
  }, [dispatch, page, rowsPerPage, sort.order, sort.sortBy, filters]);

  const handleDeleteRow = useCallback(
    async (order: IStationeryPurchaseOrderListItem, index: number) => {
      dispatch(removeStationeryPurchaseOrder({ orderId: order._id }));

      try {
        await dispatch(deleteStationeryPurchaseOrder({ order, index })).unwrap();

        fetchPaginatedOrders();
        enqueueSnackbar('Purchase order deleted');
      } catch (e) {
        if (typeof e === 'string') {
          enqueueSnackbar(e, {
            variant: 'error',
          });
        } else {
          enqueueSnackbar(e?.message, {
            variant: 'error',
          });
        }
      }
    },
    [dispatch, fetchPaginatedOrders]
  );

  useEffect(() => {
    fetchPaginatedOrders();
  }, [fetchPaginatedOrders]);

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <CustomBreadcrumbs
        heading="Stationery Purchase Orders"
        links={[]}
        action={
          <>
            {hasPermission(
              authUser,
              'any',
              AuthPermissions.CREATE_STATIONERY_PURCHASE_ORDER,
              AuthPermissions.ADMIN_PERMISSION,
              AuthPermissions.SUPER_ADMIN_PERMISSION
            ) && (
              <Button
                component={RouterLink}
                href={paths.dashboard.stationeryPurchaseOrder.new}
                variant="contained"
                startIcon={<Iconify icon="mingcute:add-line" />}
              >
                New Purchase Order
              </Button>
            )}
          </>
        }
        sx={{
          mb: { xs: 3 },
        }}
      />

      <Card>
        <StationeryPurchaseOrderTableToolbar
          filters={filters}
          onFilters={handleFilters}
          handleResetFilters={handleResetFilters}
          canReset={canReset}
        />

        <TableContainer sx={{ position: 'relative', overflow: 'hidden', height: '450px' }}>
          <Scrollbar>
            <Table size={table.dense ? 'small' : 'medium'} sx={{ minWidth: 960 }}>
              <TableHeadCustom
                order={sort.order}
                orderBy={sort.sortBy}
                headLabel={TABLE_HEAD}
                rowCount={orders.items.length}
                numSelected={table.selected.length}
                onSort={(id) => {
                  if (id === sort.sortBy) {
                    setSort((prev) => ({
                      ...prev,
                      order: prev.order === 'asc' ? 'desc' : 'asc',
                    }));
                  } else {
                    setSort({
                      sortBy: id,
                      order: 'asc',
                    });
                  }
                }}
              />

              <TableBody>
                {status === 'loading' ? (
                  [...Array(table.rowsPerPage)].map((i, index) => (
                    <TableSkeleton key={index} sx={{ height: denseHeight }} />
                  ))
                ) : (
                  <>
                    {orders?.items.map((row: IStationeryPurchaseOrderListItem, index: number) => (
                      <StationeryPurchaseOrderTableRow
                        key={row._id}
                        row={row}
                        isEditable={canDetermineLastOrder && index === 0}
                        onDeleteRow={() => handleDeleteRow(row, index)}
                        onEditRow={() => handleEditRow(row._id)}
                      />
                    ))}
                  </>
                )}

                {status !== 'failure' && orders.count === 0 && (
                  <TableNoData
                    message="No purchase orders found"
                    notFound={orders.count === 0}
                  />
                )}

                {status === 'failure' && <TableErrorData notFound error={error} />}
              </TableBody>
            </Table>
          </Scrollbar>
        </TableContainer>

        <TablePaginationCustom
          count={orders.count}
          page={table.page}
          rowsPerPage={table.rowsPerPage}
          onPageChange={table.onChangePage}
          onRowsPerPageChange={table.onChangeRowsPerPage}
          dense={table.dense}
          onChangeDense={table.onChangeDense}
        />
      </Card>
    </Container>
  );
}
